import React from 'react';
import { motion } from 'framer-motion';
import PageLayout from '../../components/layout/PageLayout';
import Section from '../../components/layout/Section';
import HolographicText from '../../components/ui/HolographicText';
import FuturisticCard from '../../components/ui/FuturisticCard';
import ParticleSystem from '../../components/ui/ParticleSystem';
import FloatingElements from '../../components/ui/FloatingElements';
import FuturisticDataViz from '../../components/ui/FuturisticDataViz';
import { COMPANY_INFO, CORE_VALUES, INDUSTRY_EXPERTISE } from '../../constants/company';

const AboutUs = () => {
  const companyStats = [
    { label: 'Years Working Together', value: COMPANY_INFO.stats.yearsExperience },
    { label: 'Government Projects', value: COMPANY_INFO.stats.governmentProjects },
    { label: 'Client Success Rate', value: COMPANY_INFO.stats.successRate },
    { label: 'End-to-End Solutions', value: COMPANY_INFO.stats.endToEndSolutions }
  ];

  return (
    <PageLayout
      title="About Us - Ons Mettle Corporate"
      description="Discover our journey of innovation, excellence, and transformation. Learn about our mission to drive digital evolution."
      keywords={['about us', 'company story', 'mission', 'vision', 'team', 'expertise']}
      // Changed background to a light, subtle gradient
      backgroundClass="bg-gradient-to-b from-gray-50 via-gray-100 to-gray-200 text-gray-900"
      mainClassName="pt-20 relative overflow-hidden"
    >
      {/* Background Effects (ParticleSystem and FloatingElements colors might need adjustment to show on lighter background, but their opacity is low) */}
      <ParticleSystem
        particleCount={60}
        color="#00D4FF" // This color might be less visible on a light background, consider darker accent if needed
        size={2}
        speed={0.3}
        className="opacity-40"
      />
      <FloatingElements count={8} className="opacity-20" />

      {/* Hero Section */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            {/* HolographicText component might need internal adjustment if it hardcodes light text,
                otherwise, the surrounding text color will apply a dark shade. */}
            <HolographicText
              size="text-3xl sm:text-4xl md:text-5xl lg:text-6xl"
              className="mb-8 text-gray-900" // Explicitly set text to black shade
              animate={true}
            >
              About Ons Mettle
            </HolographicText>
            <motion.p
              // Changed text to a dark shade for contrast on light background
              className="text-lg sm:text-xl lg:text-2xl text-gray-800 font-body max-w-4xl mx-auto leading-relaxed px-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              Founded by experienced professionals with over 10 years of collaborative expertise in{' '}
              <span className="text-brand-cyan font-semibold">engineering and telecommunications</span>,
              we provide comprehensive end-to-end services and solutions that connect clients to tomorrow's technology today.
            </motion.p>
          </motion.div>
        </div>
      </section>

      ---

      {/* Company Stats */}
      <section className="py-16 relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            {/* Assuming FuturisticDataViz handles its internal text colors or it's designed for light backgrounds already */}
            <FuturisticDataViz
              data={companyStats}
              type="circular"
              color="#00D4FF"
              // Ensure glass-morph also works with light backgrounds (usually it does by blending)
              className="glass-morph rounded-2xl bg-black border border-gray-300" // Added light background for glass-morph
            />
          </motion.div>
        </div>
      </section>

      ---

      {/* Mission & Vision */}
      <section className="py-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
            >
              {/* FuturisticCard variant="primary" usually implies a primary brand color. 
                  We'll ensure the text inside is dark on this card's lighter background. */}
              <FuturisticCard variant="primary" glow={true} className="p-8 h-full bg-white/80 border border-brand-cyan/20">
                <h3 className="text-2xl sm:text-3xl font-bold font-heading mb-6 text-gray-900"> {/* Changed to dark shade */}
                  Our Mission
                </h3>
                {/* Changed text to dark shade */}
                <p className="text-gray-800 font-body leading-relaxed text-lg">
                  Designing, developing, and delivering{' '}
                  <span className="text-brand-cyan font-semibold">advanced telecom solutions</span>{' '}
                  that connect our clients to tomorrow's technology today. We focus on innovation,
                  engineering excellence, and responsive systems to create comprehensive end-to-end
                  services that drive digital transformation.
                </p>
              </FuturisticCard>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
            >
              {/* FuturisticCard variant="secondary" usually implies a secondary brand color. 
                  We'll ensure the text inside is dark on this card's lighter background. */}
              <FuturisticCard variant="secondary" glow={true} className="p-8 h-full bg-white/80 border border-brand-green/20">
                <h3 className="text-2xl sm:text-3xl font-bold font-heading mb-6 text-gray-900"> {/* Changed to dark shade */}
                  Our Vision
                </h3>
                {/* Changed text to dark shade */}
                <p className="text-gray-800 font-body leading-relaxed text-lg">
                  Connecting clients to tomorrow's technology today through{' '}
                  <span className="text-brand-green font-semibold">innovation, engineering excellence, and responsive systems</span>.
                  We envision a future where advanced telecommunications solutions enable seamless connectivity
                  and drive meaningful digital transformation across industries and communities.
                </p>
              </FuturisticCard>
            </motion.div>
          </div>
        </div>
      </section>

      ---

      {/* Core Values */}
      <section className="py-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <HolographicText
              size="text-2xl sm:text-3xl lg:text-4xl"
              className="mb-4 text-gray-900" // Explicitly set text to black shade
            >
              Core Values
            </HolographicText>
            {/* Changed text to dark shade */}
            <p className="text-gray-700 font-body max-w-2xl mx-auto">
              The principles that guide our journey towards excellence
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {CORE_VALUES.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                {/* FuturisticCard variant="default" will now have a light background for dark text */}
                <FuturisticCard
                  variant="default"
                  className="p-6 text-center h-full hover:scale-105 transition-transform duration-300 bg-white/80 border border-gray-200"
                >
                  {/* Assuming value.icon is an emoji or SVG that renders well */}
                  <div className="text-4xl mb-4 text-gray-800">{value.icon}</div> {/* Changed to dark shade */}
                  <h4 className="text-xl font-bold font-heading mb-4 text-brand-cyan"> {/* Brand color text retained */}
                    {value.title}
                  </h4>
                  {/* Changed text to dark shade */}
                  <p className="text-gray-700 font-body text-sm leading-relaxed">
                    {value.description}
                  </p>
                </FuturisticCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      ---

      {/* Industry Expertise */}
      <section className="py-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <HolographicText
              size="text-2xl sm:text-3xl lg:text-4xl"
              className="mb-4 text-gray-900" // Explicitly set text to black shade
            >
              Industry Expertise
            </HolographicText>
            {/* Changed text to dark shade */}
            <p className="text-gray-700 font-body max-w-2xl mx-auto">
              Deep roots in engineering and telecommunications with comprehensive service capabilities
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {INDUSTRY_EXPERTISE.map((expertise, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                {/* FuturisticCard variant="default" will have a light background for dark text */}
                <FuturisticCard
                  variant="default"
                  className="p-6 text-center h-full hover:scale-105 transition-transform duration-300 bg-white/80 border border-gray-200"
                >
                  {/* Icon and title colors are pulled from expertise.color, assuming they are vibrant enough for light backgrounds */}
                  <div className={`text-4xl mb-4 ${expertise.color}`}>{expertise.icon}</div>
                  <h4 className={`text-xl font-bold font-heading mb-4 ${expertise.color}`}>
                    {expertise.title}
                  </h4>
                  {/* Changed text to dark shade */}
                  <p className="text-gray-700 font-body text-sm leading-relaxed">
                    {expertise.description}
                  </p>
                </FuturisticCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      ---

      {/* Company Story */}
      <section className="py-16 relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            {/* FuturisticCard variant="primary" will have a light background for dark text */}
            <FuturisticCard variant="primary" glow={true} className="p-8 sm:p-12 bg-white/80 border border-brand-cyan/20">
              <div className="text-center mb-8">
                <HolographicText
                  size="text-2xl sm:text-3xl lg:text-4xl"
                  className="mb-6 text-gray-900" // Explicitly set text to black shade
                >
                  Our Journey
                </HolographicText>
              </div>

              {/* Changed text to dark shade */}
              <div className="space-y-6 text-gray-800 font-body leading-relaxed">
                <p className="text-lg">
                  ONS METTLE was founded by a team of experienced professionals who have been working
                  together for over{' '}
                  <span className="text-brand-cyan font-semibold">10 years</span>.
                  With deep roots in{' '}
                  <span className="text-brand-green font-semibold">engineering and telecommunications</span>,
                  we bring a spirit of innovation and an entrepreneurial mindset to everything we do.
                </p>

                <p className="text-lg">
                  What sets us apart is our unwavering focus on{' '}
                  <span className="text-brand-cyan font-semibold">customer-centricity, innovation, and trust</span>.
                  We provide comprehensive end-to-end services and solutions, ensuring that every project
                  receives the full attention of our dedicated team from start to finish.
                </p>

                <p className="text-lg">
                  Our organizational approach features a{' '}
                  <span className="text-brand-orange font-semibold">flat structure with client-first methodology</span>,
                  where every project is handled by a Project Head, Project Manager, and Expert Team.
                  This ensures full involvement and accountability throughout the entire project lifecycle.
                </p>

                <p className="text-lg">
                  By embracing cutting-edge technology and maintaining our commitment to excellence,
                  we deliver{' '}
                  <span className="text-brand-purple font-semibold">advanced telecom solutions</span>{' '}
                  that connect our clients to tomorrow's technology today, building the foundation
                  for sustainable digital transformation.
                </p>
              </div>
            </FuturisticCard>
          </motion.div>
        </div>
      </section>

      ---

      {/* Leadership & Team Structure */}
      <section className="py-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <HolographicText
              size="text-2xl sm:text-3xl lg:text-4xl"
              className="mb-4 text-gray-900" // Explicitly set text to black shade
            >
              Leadership & Team Structure
            </HolographicText>
            {/* Changed text to dark shade */}
            <p className="text-gray-700 font-body max-w-2xl mx-auto">
              Flat organizational structure with client-first methodology ensuring dedicated project success
            </p>
          </motion.div>

          {/* Organizational Approach */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
            >
              {/* FuturisticCard variant="primary" will have a light background for dark text */}
              <FuturisticCard variant="primary" glow={true} className="p-8 h-full bg-white/80 border border-brand-cyan/20">
                <h3 className="text-2xl sm:text-3xl font-bold font-heading mb-6 text-gray-900"> {/* Changed to dark shade */}
                  Organizational Approach
                </h3>
                {/* Changed text to dark shade */}
                <div className="space-y-4 text-gray-800 font-body leading-relaxed">
                  <p className="text-lg">
                    Our{' '}
                    <span className="text-brand-cyan font-semibold">flat organizational structure</span>{' '}
                    promotes direct communication, faster decision-making, and enhanced collaboration
                    across all levels of the organization.
                  </p>
                  <p className="text-lg">
                    We follow a{' '}
                    <span className="text-brand-green font-semibold">client-first methodology</span>{' '}
                    where every decision and action is aligned with delivering maximum value to our clients.
                  </p>
                </div>
              </FuturisticCard>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
            >
              {/* FuturisticCard variant="secondary" will have a light background for dark text */}
              <FuturisticCard variant="secondary" glow={true} className="p-8 h-full bg-white/80 border border-brand-green/20">
                <h3 className="text-2xl sm:text-3xl font-bold font-heading mb-6 text-gray-900"> {/* Changed to dark shade */}
                  Project Management
                </h3>
                {/* Changed text to dark shade */}
                <div className="space-y-4 text-gray-800 font-body leading-relaxed">
                  <p className="text-lg">
                    Every project is handled by a dedicated team consisting of:
                  </p>
                  <ul className="space-y-2 text-lg">
                    <li className="flex items-center">
                      <span className="text-brand-orange mr-2">•</span>
                      <span className="text-brand-orange font-semibold">Project Head</span> - Strategic oversight
                    </li>
                    <li className="flex items-center">
                      <span className="text-brand-cyan mr-2">•</span>
                      <span className="text-brand-cyan font-semibold">Project Manager</span> - Execution management
                    </li>
                    <li className="flex items-center">
                      <span className="text-brand-green mr-2">•</span>
                      <span className="text-brand-green font-semibold">Expert Team</span> - Technical implementation
                    </li>
                  </ul>
                </div>
              </FuturisticCard>
            </motion.div>
          </div>

          {/* Organizational Chart */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            {/* FuturisticCard variant="default" will have a light background for dark text */}
            <FuturisticCard variant="default" className="p-8 bg-white/80 border border-gray-200">
              <h3 className="text-2xl sm:text-3xl font-bold font-heading mb-8 text-center text-gray-900"> {/* Changed to dark shade */}
                Organizational Structure
              </h3>

              <div className="space-y-8">
                {/* Top Level - Background gradients remain, ensuring text is white for contrast */}
                <div className="text-center">
                  <div className="inline-block bg-gradient-to-r from-brand-cyan to-brand-blue px-6 py-3 rounded-lg">
                    <span className="text-white font-bold text-lg">Business & Operations Head</span>
                  </div>
                </div>

                {/* Second Level - Background gradients remain, ensuring text is white for contrast */}
                <div className="flex justify-center items-center space-x-8">
                  <div className="bg-gradient-to-r from-brand-orange to-brand-red px-4 py-2 rounded-lg">
                    <span className="text-white font-semibold">Project Heads</span>
                  </div>
                  <div className="bg-gradient-to-r from-brand-green to-brand-teal px-4 py-2 rounded-lg">
                    <span className="text-white font-semibold">Project Managers</span>
                  </div>
                </div>

                {/* Third Level - Background gradients remain, ensuring text is white for contrast */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-center">
                  {['Engineers', 'MIS', 'QA', 'Liaising', 'Client Support', 'Operations'].map((role, index) => (
                    <div key={index} className="bg-gradient-to-r from-brand-purple to-brand-navy px-3 py-2 rounded-lg">
                      <span className="text-white text-sm font-medium">{role}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 text-center">
                {/* Changed text to dark shade */}
                <p className="text-gray-700 font-body">
                  <span className="text-brand-cyan font-semibold">Full involvement</span> from project start to finish
                  ensures accountability and quality delivery
                </p>
              </div>
            </FuturisticCard>
          </motion.div>
        </div>
      </section>

      ---

      {/* Credentials & Documentation */}
      <section className="py-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <HolographicText
              size="text-2xl sm:text-3xl lg:text-4xl"
              className="mb-4 text-gray-900" // Explicitly set text to black shade
            >
              Credentials & Documentation
            </HolographicText>
            {/* Changed text to dark shade */}
            <p className="text-gray-700 font-body max-w-2xl mx-auto">
              Comprehensive documentation and certifications validating our expertise and compliance
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Work Orders & Experience */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
            >
              {/* FuturisticCard variant="primary" will have a light background for dark text */}
              <FuturisticCard variant="primary" glow={true} className="p-8 h-full bg-white/80 border border-brand-cyan/20">
                <h3 className="text-2xl sm:text-3xl font-bold font-heading mb-6 text-gray-900"> {/* Changed to dark shade */}
                  Work Orders & Experience
                </h3>
                {/* Changed text to dark shade */}
                <div className="space-y-4 text-gray-800 font-body leading-relaxed">
                  <div className="flex items-start space-x-3">
                    <span className="text-brand-cyan text-xl mt-1">📋</span>
                    <div>
                      <h4 className="font-semibold text-brand-cyan mb-1">PHED Work Orders</h4>
                      <p className="text-sm">Comprehensive work orders and experience certificates for Madhya Pradesh Public Health Engineering Department projects</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-brand-green text-xl mt-1">🤝</span>
                    <div>
                      <h4 className="font-semibold text-brand-green mb-1">BPCL Agreements</h4>
                      <p className="text-sm">Multi-state project agreements and completion certificates for Bharat Petroleum Corporation Limited</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-brand-orange text-xl mt-1">🏥</span>
                    <div>
                      <h4 className="font-semibold text-brand-orange mb-1">AIIMS Work Order</h4>
                      <p className="text-sm">Project documentation and completion certificates for AIIMS Deoghar infrastructure development</p>
                    </div>
                  </div>
                </div>
              </FuturisticCard>
            </motion.div>

            {/* Certifications */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
            >
              {/* FuturisticCard variant="secondary" will have a light background for dark text */}
              <FuturisticCard variant="secondary" glow={true} className="p-8 h-full bg-white/80 border border-brand-green/20">
                <h3 className="text-2xl sm:text-3xl font-bold font-heading mb-6 text-gray-900"> {/* Changed to dark shade */}
                  Certifications & Compliance
                </h3>
                {/* Changed text to dark shade */}
                <div className="space-y-4 text-gray-800 font-body leading-relaxed">
                  <div className="flex items-start space-x-3">
                    <span className="text-brand-purple text-xl mt-1">🏢</span>
                    <div>
                      <h4 className="font-semibold text-brand-purple mb-1">Incorporation Certificate</h4>
                      <p className="text-sm">Official company incorporation and registration documentation</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-brand-cyan text-xl mt-1">👥</span>
                    <div>
                      <h4 className="font-semibold text-brand-cyan mb-1">EPF & ESIC Registration</h4>
                      <p className="text-sm">Employee Provident Fund and Employee State Insurance Corporation compliance</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-brand-green text-xl mt-1">💼</span>
                    <div>
                      <h4 className="font-semibold text-brand-green mb-1">GST Registration</h4>
                      <p className="text-sm">Goods and Services Tax registration and compliance documentation</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-brand-orange text-xl mt-1">⭐</span>
                    <div>
                      <h4 className="font-semibold text-brand-orange mb-1">ISO Certifications</h4>
                      <p className="text-sm">International Organization for Standardization quality management certifications</p>
                    </div>
                  </div>
                </div>
              </FuturisticCard>
            </motion.div>
          </div>

          {/* Compliance Statement */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            {/* FuturisticCard variant="default" will have a light background for dark text */}
            <FuturisticCard variant="default" className="p-8 bg-white/80 border border-gray-200">
              <div className="text-center">
                <h4 className="text-xl font-bold font-heading mb-4 text-gray-900"> {/* Changed to dark shade */}
                  Compliance & Quality Assurance
                </h4>
                {/* Changed text to dark shade */}
                <p className="text-gray-800 font-body leading-relaxed max-w-4xl mx-auto">
                  ONS METTLE maintains{' '}
                  <span className="text-brand-cyan font-semibold">comprehensive documentation</span>{' '}
                  and{' '}
                  <span className="text-brand-green font-semibold">regulatory compliance</span>{' '}
                  across all our operations. Our certifications and work orders demonstrate our commitment to{' '}
                  <span className="text-brand-orange font-semibold">quality standards</span>,{' '}
                  <span className="text-brand-purple font-semibold">legal compliance</span>, and{' '}
                  <span className="text-brand-cyan font-semibold">professional excellence</span>{' '}
                  in every project we undertake.
                </p>
              </div>
            </FuturisticCard>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  );
};

export default AboutUs;